import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import firebase from "../../../database/firebase";
import DB_PATH from "../../../database/DB_PATH";
import { Button } from "../../../design-system";
import { Author } from "../../../types/Author";
import {
  AdPlatform,
  CommunityAdFormat,
} from "../../../types/CommunityMediaConfig";
import {
  getCommunityMediaConfig,
  researchAdFormatsForAudience,
} from "../../../database/community/mediaConfig";
import {
  generateAdScript,
  generateAdCopyVariants,
  generateAdImage,
  adCopyVariantsToCsv,
  AdCopyVariant,
  AdAngleCategory,
  AdPlacement,
  AD_PLATFORM_SPECS,
  AD_PLACEMENT_SPECS,
  PLACEMENTS_FOR_PLATFORM,
} from "../../../database/community/adsGenerator";
import {
  useAdsGeneratorState,
  VariantSelection,
} from "./sections/create/ads/useAdsGeneratorState";
import {
  saveAdVariant,
  listSavedAdVariants,
  deleteSavedAdVariant,
  SavedAdVariant,
} from "../../../database/community/adVariants";
import {
  uploadCompetitorReferenceAd,
  deleteCompetitorReferenceAd,
  listCompetitorReferenceAds,
  fetchImageAsDataUrl,
} from "../../../database/community/competitorReferenceAds";
import { CompetitorReferenceAd } from "../../../types/CommunityMediaConfig";
import { GEMINI_API_KEY } from "../plan/dayplan/components/dailyStep/mediaGeneratorConfig";

// ─── Local styled UI (kept simple — this is a testable build) ─────────────

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px;
  width: 100%;
  max-width: 100%;
  min-height: 0;
  overflow-y: auto;
  color: var(--color-text-primary);
`;

const SectionCard = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-subtle);
  border-radius: 12px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--color-text-primary);
`;

const SectionSub = styled.div`
  font-size: 12px;
  color: var(--color-text-secondary);
  line-height: 1.5;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 10px 12px;
  font: inherit;
  font-size: 13px;
  line-height: 1.45;
  background: var(--color-bg-canvas);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-subtle);
  border-radius: 8px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const Chip = styled.button<{ $active: boolean }>`
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 500;
  background: ${(p) => (p.$active ? "var(--color-accent)" : "transparent")};
  color: ${(p) =>
    p.$active ? "var(--color-on-accent)" : "var(--color-text-primary)"};
  border: 1px solid
    ${(p) => (p.$active ? "var(--color-accent)" : "var(--color-border-subtle)")};
  border-radius: 999px;
  cursor: pointer;

  &:hover {
    border-color: var(--color-accent);
  }
`;

const NumberField = styled.input.attrs({ type: "number", min: 1, max: 5 })`
  width: 56px;
  padding: 6px 8px;
  font-size: 12px;
  background: var(--color-bg-canvas);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-subtle);
  border-radius: 6px;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const SmallSelect = styled.select`
  padding: 4px 6px;
  font-size: 11px;
  background: var(--color-bg-canvas);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-subtle);
  border-radius: 6px;
  max-width: 180px;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const TextField = styled.input.attrs({ type: "text" })`
  width: 140px;
  padding: 6px 8px;
  font-size: 12px;
  background: var(--color-bg-canvas);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-subtle);
  border-radius: 6px;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
`;

const ImageThumbWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-subtle);
  border-radius: 8px;
`;

const ImageThumb = styled.img`
  width: 100%;
  height: auto;
  border-radius: 6px;
  display: block;
`;

const ImageThumbCaption = styled.div`
  font-size: 10px;
  color: var(--color-text-secondary);
  line-height: 1.3;
`;

const HintCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  background: var(--color-bg-elevated);
  border: 1px dashed var(--color-border-subtle);
  border-radius: 8px;
  font-size: 11px;
  color: var(--color-text-secondary);
  line-height: 1.5;
`;

const HintTitle = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-primary);
`;

const HiddenFileInput = styled.input.attrs({ type: "file", accept: "image/*" })`
  display: none;
`;

const RefThumbCard = styled.div<{ $failed?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px;
  background: var(--color-bg-elevated);
  border: 1px solid
    ${(p) => (p.$failed ? "var(--color-danger)" : "var(--color-border-subtle)")};
  border-radius: 8px;
  position: relative;
`;

const RefFailureBadge = styled.div`
  font-size: 10px;
  color: var(--color-danger);
  font-weight: 600;
  line-height: 1.3;
`;

const RefThumbImg = styled.img`
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: 6px;
`;

const RefThumbMeta = styled.div`
  font-size: 10px;
  color: var(--color-text-secondary);
  line-height: 1.3;
`;

const RefThumbDelete = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const AuthorRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const AuthorChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 4px;
  background: ${(p) =>
    p.$active ? "var(--color-accent)" : "transparent"};
  color: ${(p) =>
    p.$active ? "var(--color-on-accent)" : "var(--color-text-primary)"};
  border: 1px solid
    ${(p) => (p.$active ? "var(--color-accent)" : "var(--color-border-subtle)")};
  border-radius: 999px;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    border-color: var(--color-accent);
  }
`;

const AuthorAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  background: var(--color-bg-elevated);
`;

const AuthorAvatarFallback = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  background: var(--color-bg-elevated);
  color: var(--color-text-secondary);
`;

const Error = styled.div`
  font-size: 12px;
  color: var(--color-danger);
  background: var(--color-danger-bg, rgba(255, 80, 80, 0.08));
  border: 1px solid var(--color-danger);
  border-radius: 6px;
  padding: 8px 10px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const FormatCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: var(--color-bg-canvas);
  border: 1px solid var(--color-border-subtle);
  border-radius: 10px;
`;

const FormatHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

const FormatLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const Badge = styled.span<{ $tone?: "accent" | "muted" | "warn" }>`
  display: inline-block;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${(p) =>
    p.$tone === "warn"
      ? "var(--color-warning-bg, rgba(255,180,0,0.18))"
      : p.$tone === "muted"
      ? "var(--color-bg-elevated)"
      : "var(--color-accent-bg, rgba(120,120,255,0.18))"};
  color: ${(p) =>
    p.$tone === "warn"
      ? "var(--color-warning, #c08400)"
      : p.$tone === "muted"
      ? "var(--color-text-secondary)"
      : "var(--color-accent, inherit)"};
  border-radius: 4px;
`;

const FormatStructure = styled.pre`
  margin: 0;
  font: inherit;
  font-size: 11px;
  line-height: 1.5;
  color: var(--color-text-secondary);
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
`;

const BeatRow = styled.div`
  display: grid;
  grid-template-columns: 60px 80px 1fr;
  gap: 8px;
  padding: 8px;
  background: var(--color-bg-elevated);
  border-radius: 6px;
  font-size: 12px;
`;

const BeatTime = styled.div`
  font-weight: 600;
  color: var(--color-accent);
`;

const BeatRole = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  font-weight: 600;
  color: var(--color-text-secondary);
  align-self: center;
`;

const BeatBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: var(--color-text-primary);
`;

const BeatLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  margin-right: 4px;
`;

const VariantGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 10px;
`;

const VariantCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
  background: var(--color-bg-canvas);
  border: 1px solid var(--color-border-subtle);
  border-radius: 8px;
  font-size: 12px;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FieldHeader = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

const FieldBody = styled.div`
  font-size: 12px;
  color: var(--color-text-primary);
  white-space: pre-wrap;
  word-break: break-word;
`;

// ─── Platform + angle constants ──────────────────────────────────────────

const PLATFORMS: AdPlatform[] = ["meta", "tiktok"];
const ANGLES: AdAngleCategory[] = [
  "pain-point",
  "outcome",
  "social-proof",
  "curiosity",
  "comparison",
  "urgency",
  "identity",
  "contrarian",
];

// ─── Component ─────────────────────────────────────────────────────────────

type Props = {
  communityId?: string;
  hasAudienceData: boolean;
};

const AdsGenerator: React.FC<Props> = ({ communityId, hasAudienceData }) => {
  const { state, actions } = useAdsGeneratorState();
  const {
    campaign,
    selectedPlatforms,
    selectedAngles,
    variationsPerAngle,
    imageProvider,
    namingObjective,
    namingAudience,
    namingOffer,
    landingSummary,
  } = state.inputs;
  const {
    adFormats,
    adFormatProfile,
    discoveries,
    researchRationale,
    researchSources,
  } = state.formats;
  const {
    activeScripts,
    variants,
    variantSelections,
    variantImages,
    variantSaving,
    savedVariantIds,
    variantTextOnImage,
    variantOverlayText,
  } = state.generation;
  const { savedVariants, isLoadingSaved } = state.library;
  const {
    competitorRefs,
    isUploadingRef,
    newRefLabel,
    newRefSource,
    refDataUrlCache,
    refFetchFailures,
    lastRefStats,
  } = state.refs;
  const { authors, selectedAuthorId, authorRefDataUrl, isLoadingAuthorRef } =
    state.authors;
  const {
    isResearching,
    scriptLoadingKey,
    imageLoadingVariantIdx,
    isGeneratingCopy,
    errorMsg,
  } = state.status;

  // Load saved adFormats + adFormatProfile from mediaConfig.
  useEffect(() => {
    if (!communityId) return;
    let cancelled = false;
    (async () => {
      try {
        const cfg = await getCommunityMediaConfig(communityId);
        if (cancelled) return;
        actions.setAdFormats(cfg.adFormats || []);
        actions.setAdFormatProfile(cfg.adFormatProfile);
      } catch (e) {
        if (cancelled) return;
        console.error("Failed to load adFormats:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [communityId, actions]);

  // Pull a small summary of the landing page so the UI can decide whether
  // the campaign textarea is truly required.
  useEffect(() => {
    if (!communityId) {
      actions.setLandingSummary(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await firebase
          .firestore()
          .collection(DB_PATH.COMMUNITY)
          .doc(communityId)
          .collection(DB_PATH.LANDING_PAGE)
          .doc("info")
          .get();
        if (cancelled) return;
        if (!snap.exists) {
          actions.setLandingSummary(null);
          return;
        }
        const data = (snap.data() || {}) as Record<string, unknown>;
        actions.setLandingSummary({
          title: typeof data.title === "string" ? data.title : "",
          subtitle: typeof data.subtitle === "string" ? data.subtitle : "",
          buttonText: typeof data.buttonText === "string" ? data.buttonText : "",
        });
      } catch (e) {
        if (!cancelled) console.error("Failed to load landing page:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [communityId, actions]);

  // Load saved ad variants for the library section.
  useEffect(() => {
    if (!communityId) {
      actions.setSavedVariants([]);
      return;
    }
    let cancelled = false;
    actions.setIsLoadingSaved(true);
    (async () => {
      try {
        const list = await listSavedAdVariants(communityId);
        if (!cancelled) actions.setSavedVariants(list);
      } catch (e) {
        if (!cancelled) console.error("Failed to load saved ad variants:", e);
      } finally {
        if (!cancelled) actions.setIsLoadingSaved(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [communityId, actions]);

  // Load competitor reference ads.
  useEffect(() => {
    if (!communityId) {
      actions.clearCompetitorRefs();
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await listCompetitorReferenceAds(communityId);
        if (cancelled) return;
        actions.setCompetitorRefs(list);
        // Eager pre-fetch each ref into the cache. This populates the cache
        // for the first image gen AND exposes any CORS / network failures
        // on the thumbnail itself, before the user clicks Generate.
        list.forEach((ref) => prefetchRefIntoCache(ref));
      } catch (e) {
        if (!cancelled) console.error("Failed to load competitor refs:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId]);

  // Load community authors (for the face-anchor picker).
  useEffect(() => {
    if (!communityId) {
      actions.setAuthors([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await firebase
          .firestore()
          .collection(DB_PATH.AUTHOR)
          .where("communityId", "==", communityId)
          .get();
        if (cancelled) return;
        const list = snap.docs
          .map((doc) => doc.data() as Author)
          .sort(
            (a, b) =>
              (a.position ?? Number.MAX_SAFE_INTEGER) -
              (b.position ?? Number.MAX_SAFE_INTEGER)
          );
        actions.setAuthors(list);
      } catch (e) {
        if (cancelled) return;
        console.error("Failed to load authors:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [communityId, actions]);

  // When the picked author changes, fetch their photo and convert to data URL
  // so it can be passed as personReferenceImage to gpt-image-1 / Gemini.
  useEffect(() => {
    if (!selectedAuthorId) {
      actions.setAuthorRefDataUrl("");
      return;
    }
    const author = authors.find((a) => a.id === selectedAuthorId);
    if (!author?.profilePhotoURL) {
      actions.setAuthorRefDataUrl("");
      return;
    }
    let cancelled = false;
    actions.setIsLoadingAuthorRef(true);
    (async () => {
      try {
        if (author.profilePhotoURL.startsWith("data:")) {
          if (!cancelled) actions.setAuthorRefDataUrl(author.profilePhotoURL);
          return;
        }
        const response = await fetch(author.profilePhotoURL);
        if (!response.ok) throw new Error("Author photo fetch failed");
        const blob = await response.blob();
        const reader = new FileReader();
        const dataUrl: string = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });
        if (!cancelled) actions.setAuthorRefDataUrl(dataUrl);
      } catch (e) {
        if (!cancelled) {
          console.error("Failed to load author reference image:", e);
          actions.setAuthorRefDataUrl("");
        }
      } finally {
        if (!cancelled) actions.setIsLoadingAuthorRef(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedAuthorId, authors, actions]);

  const togglePlatform = (p: AdPlatform) => {
    actions.togglePlatform(p);
  };

  const toggleAngle = (a: AdAngleCategory) => {
    actions.toggleAngle(a);
  };

  const handleResearchFormats = async () => {
    if (!communityId) return;
    actions.setIsResearching(true);
    actions.setErrorMsg("");
    try {
      const result = await researchAdFormatsForAudience({
        communityId,
        platforms: selectedPlatforms.length ? selectedPlatforms : undefined,
        apply: true,
      });
      actions.applyResearchResults({
        discoveries: result.discoveredAdFormats,
        rationale: result.rationale || "",
        sources: result.sources || [],
        profile: result.adFormatProfile,
      });
      // Refresh from store so we get persisted versions (createdAt etc.)
      const cfg = await getCommunityMediaConfig(communityId);
      actions.setAdFormats(cfg.adFormats || []);
    } catch (e: any) {
      actions.setErrorMsg(e?.message || "Failed to research ad formats.");
    } finally {
      actions.setIsResearching(false);
    }
  };

  const handleGenerateScript = async (
    format: CommunityAdFormat,
    platform: AdPlatform,
    angle: AdAngleCategory
  ) => {
    if (!communityId) return;
    if (!hasCampaignOrFallback) {
      actions.setErrorMsg(
        "Add a campaign description above, or fill in the community's landing page (title + subtitle) first."
      );
      return;
    }
    const key = `${format.key}-${platform}-${angle}`;
    actions.setScriptLoadingKey(key);
    actions.setErrorMsg("");
    try {
      const script = await generateAdScript({
        communityId,
        campaign: campaign.trim(),
        formatKey: format.key,
        platform,
        angle,
      });
      actions.scriptGenerated(key, script);
    } catch (e: any) {
      actions.setErrorMsg(e?.message || "Failed to generate script.");
    } finally {
      actions.setScriptLoadingKey(null);
    }
  };

  // ─── Competitor reference handlers ──────────────────────────────────────

  const handleUploadCompetitorRef = async (file: File | null) => {
    if (!communityId || !file) return;
    actions.setIsUploadingRef(true);
    actions.setErrorMsg("");
    try {
      const entry = await uploadCompetitorReferenceAd({
        communityId,
        file,
        label: newRefLabel.trim() || undefined,
        source: newRefSource.trim() || undefined,
      });
      actions.competitorRefUploaded(entry);
      // Eager fetch exposes CORS / network problems immediately.
      prefetchRefIntoCache(entry);
    } catch (e: any) {
      actions.setErrorMsg(e?.message || "Failed to upload reference ad.");
    } finally {
      actions.setIsUploadingRef(false);
    }
  };

  const handleDeleteCompetitorRef = async (entry: CompetitorReferenceAd) => {
    if (!communityId) return;
    try {
      await deleteCompetitorReferenceAd(communityId, entry.id);
      actions.competitorRefDeleted(entry);
    } catch (e: any) {
      actions.setErrorMsg(e?.message || "Failed to delete reference ad.");
    }
  };

  /**
   * Resolves the competitor references to data URLs, using/populating the
   * session cache so repeated image gens don't re-fetch. Tracks failures so
   * the UI can show what didn't make it (usually CORS on the Storage bucket).
   */
  const resolveCompetitorRefDataUrls = async (
    forPlatform: AdPlatform
  ): Promise<{ dataUrls: string[]; attempted: number; failures: string[] }> => {
    const candidates = competitorRefs.filter(
      (r) => !r.platform || r.platform === forPlatform
    );
    if (candidates.length === 0)
      return { dataUrls: [], attempted: 0, failures: [] };

    const updates: Record<string, string> = {};
    const failureUpdates: Record<string, string> = {};
    const dataUrls: string[] = [];
    const failures: string[] = [];

    await Promise.all(
      candidates.map(async (ref) => {
        const cached = refDataUrlCache[ref.imageURL];
        if (cached) {
          dataUrls.push(cached);
          return;
        }
        try {
          const dataUrl = await fetchImageAsDataUrl(ref.imageURL);
          if (dataUrl) {
            updates[ref.imageURL] = dataUrl;
            dataUrls.push(dataUrl);
          } else {
            failures.push(ref.label || ref.imageURL);
            failureUpdates[ref.imageURL] = "Empty response";
          }
        } catch (e: any) {
          const msg = e?.message || "Failed to fetch";
          console.warn("Failed to fetch competitor ref:", ref.imageURL, e);
          failures.push(ref.label || ref.imageURL);
          failureUpdates[ref.imageURL] = msg;
        }
      })
    );
    if (Object.keys(updates).length > 0) {
      actions.mergeRefDataUrlCache(updates);
    }
    if (Object.keys(failureUpdates).length > 0) {
      actions.mergeRefFetchFailures(failureUpdates);
    }
    return { dataUrls, attempted: candidates.length, failures };
  };

  // Eager pre-fetch when a ref is added — surfaces CORS / fetch failures
  // immediately instead of waiting until the user clicks Generate.
  const prefetchRefIntoCache = async (ref: CompetitorReferenceAd) => {
    if (refDataUrlCache[ref.imageURL]) return;
    try {
      const dataUrl = await fetchImageAsDataUrl(ref.imageURL);
      if (dataUrl) {
        actions.refPrefetched(ref.imageURL, dataUrl);
      }
    } catch (e: any) {
      const msg = e?.message || "Failed to fetch";
      console.warn("Pre-fetch failed:", ref.imageURL, e);
      actions.mergeRefFetchFailures({ [ref.imageURL]: msg });
    }
  };

  const getDefaultPlacement = (platform: AdPlatform): AdPlacement =>
    PLACEMENTS_FOR_PLATFORM[platform][0];

  const getVariantSelection = (
    idx: number,
    variant: AdCopyVariant
  ): VariantSelection => {
    const existing = variantSelections[idx];
    if (existing) return existing;
    const platformFormats = adFormats.filter((f) =>
      f.platforms.includes(variant.platform)
    );
    return {
      formatKey: platformFormats[0]?.key || "",
      placement: getDefaultPlacement(variant.platform),
    };
  };

  const handleGenerateVariantImage = async (
    idx: number,
    variant: AdCopyVariant
  ) => {
    if (!communityId) return;
    if (!hasCampaignOrFallback) {
      actions.setErrorMsg(
        "Add a campaign description above, or fill in the community's landing page (title + subtitle) first."
      );
      return;
    }
    const selection = getVariantSelection(idx, variant);
    if (!selection.formatKey) {
      actions.setErrorMsg(
        `No ad formats available for ${variant.platform}. Run "Find winning ad formats" first.`
      );
      return;
    }
    actions.setImageLoadingVariantIdx(idx);
    actions.setErrorMsg("");
    try {
      const refResult = await resolveCompetitorRefDataUrls(variant.platform);
      actions.setLastRefStats({
        attempted: refResult.attempted,
        succeeded: refResult.dataUrls.length,
      });
      if (refResult.attempted > 0 && refResult.dataUrls.length === 0) {
        actions.setErrorMsg(
          `Tried ${refResult.attempted} competitor reference${
            refResult.attempted === 1 ? "" : "s"
          } but NONE could be fetched (likely Storage CORS). The image was generated WITHOUT them. Check the browser console + your Firebase Storage CORS config.`
        );
      }
      const dataUrl = await generateAdImage({
        communityId,
        campaign: campaign.trim(),
        formatKey: selection.formatKey,
        platform: variant.platform,
        placement: selection.placement,
        angle: variant.angle,
        provider: imageProvider,
        geminiApiKey: imageProvider === "gemini" ? GEMINI_API_KEY : undefined,
        personReferenceImage: authorRefDataUrl || undefined,
        styleReferenceImages:
          refResult.dataUrls.length > 0 ? refResult.dataUrls : undefined,
        variantHook: variant.primaryText,
        includeTextOnImage: variantTextOnImage[idx] || false,
        customOverlayText: variantOverlayText[idx]?.trim() || undefined,
      });

      actions.setVariantImage(idx, {
        dataUrl,
        formatKey: selection.formatKey,
        placement: selection.placement,
      });
    } catch (e: any) {
      actions.setErrorMsg(e?.message || "Failed to generate ad image.");
    } finally {
      actions.setImageLoadingVariantIdx(null);
    }
  };

  const handleSaveVariant = async (idx: number, variant: AdCopyVariant) => {
    if (!communityId) return;
    actions.setVariantSaving(idx, true);
    actions.setErrorMsg("");
    try {
      const img = variantImages[idx];
      const saved = await saveAdVariant({
        communityId,
        campaign: campaign.trim(),
        variant,
        formatKey: img?.formatKey,
        placement: img?.placement,
        imageDataUrl: img?.dataUrl,
      });
      actions.variantSaved(idx, saved);
    } catch (e: any) {
      actions.setErrorMsg(e?.message || "Failed to save ad variant.");
    } finally {
      actions.setVariantSaving(idx, false);
    }
  };

  const handleDeleteSaved = async (saved: SavedAdVariant) => {
    if (!communityId) return;
    try {
      await deleteSavedAdVariant(communityId, saved.id);
      actions.savedVariantDeleted(saved.id);
    } catch (e: any) {
      actions.setErrorMsg(e?.message || "Failed to delete saved variant.");
    }
  };

  const handleGenerateCopy = async () => {
    if (!communityId) return;
    if (!hasCampaignOrFallback) {
      actions.setErrorMsg(
        "Add a campaign description above, or fill in the community's landing page (title + subtitle) first."
      );
      return;
    }
    if (selectedPlatforms.length === 0) {
      actions.setErrorMsg("Pick at least one platform.");
      return;
    }
    if (selectedAngles.length === 0) {
      actions.setErrorMsg("Pick at least one angle.");
      return;
    }
    actions.setIsGeneratingCopy(true);
    actions.setErrorMsg("");
    try {
      const newVariants = await generateAdCopyVariants({
        communityId,
        campaign: campaign.trim(),
        platforms: selectedPlatforms,
        angles: selectedAngles,
        variationsPerAngle,
      });
      actions.copyVariantsGenerated(newVariants);
    } catch (e: any) {
      actions.setErrorMsg(e?.message || "Failed to generate ad copy.");
    } finally {
      actions.setIsGeneratingCopy(false);
    }
  };

  const handleDownloadCsv = () => {
    if (variants.length === 0) return;
    const csv = adCopyVariantsToCsv(variants, {
      objective: namingObjective.trim() || undefined,
      audience: namingAudience.trim() || undefined,
      offer: namingOffer.trim() || undefined,
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ad-copy-variants-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Either the user typed a campaign brief, OR the landing page provides
  // enough to auto-derive one (title at minimum). When this is false, the
  // model genuinely has no idea what we're advertising.
  const hasCampaignOrFallback = Boolean(
    campaign.trim() || landingSummary?.title?.trim()
  );

  const profileWeightLookup = useMemo(() => {
    const map = new Map<string, number>();
    (adFormatProfile?.formats || []).forEach((f) => map.set(f.format, f.weight));
    return map;
  }, [adFormatProfile]);

  if (!communityId) {
    return (
      <Root>
        <SectionCard>
          <SectionTitle>Ads</SectionTitle>
          <SectionSub>No community selected.</SectionSub>
        </SectionCard>
      </Root>
    );
  }

  return (
    <Root>
      {/* ── Campaign + platform picker ── */}
      <SectionCard>
        <SectionTitle>Campaign (optional)</SectionTitle>
        <SectionSub>
          Leave blank to use the community's landing page as the campaign
          (title + subtitle + button). Type something here to NARROW or
          FOCUS the ads — e.g. a Black Friday push, a specific plan, a
          one-time offer.
        </SectionSub>
        <TextArea
          value={campaign}
          onChange={(e) => actions.setCampaign(e.target.value)}
          placeholder={
            landingSummary?.title
              ? `Optional. Fallback: "${landingSummary.title}${
                  landingSummary.subtitle ? `. ${landingSummary.subtitle}` : ""
                }"`
              : 'e.g. "30-day money-back guarantee on Launch Small course. $99. For first-time digital product builders who feel stuck on pricing."'
          }
        />
        {!campaign.trim() && landingSummary?.title && (
          <SectionSub style={{ fontSize: 11 }}>
            Using landing page: <strong>{landingSummary.title}</strong>
            {landingSummary.subtitle ? ` · ${landingSummary.subtitle}` : ""}
          </SectionSub>
        )}
        {!campaign.trim() && !landingSummary?.title && (
          <SectionSub
            style={{ fontSize: 11, color: "var(--color-warning, #c08400)" }}
          >
            No landing page set up for this community — type a campaign brief
            or fill in the landing page first.
          </SectionSub>
        )}
        <Row>
          <SectionSub>Platforms:</SectionSub>
          {PLATFORMS.map((p) => (
            <Chip
              key={p}
              type="button"
              $active={selectedPlatforms.includes(p)}
              onClick={() => togglePlatform(p)}
            >
              {p === "meta" ? "Meta" : "TikTok"}
            </Chip>
          ))}
          <SectionSub style={{ marginLeft: 16 }}>Image model:</SectionSub>
          <Chip
            type="button"
            $active={imageProvider === "gpt"}
            onClick={() => actions.setImageProvider("gpt")}
          >
            GPT
          </Chip>
          <Chip
            type="button"
            $active={imageProvider === "gemini"}
            onClick={() => actions.setImageProvider("gemini")}
          >
            Gemini
          </Chip>
        </Row>

        <AuthorRow>
          <SectionSub>
            Face anchor (optional):
            {isLoadingAuthorRef && " loading photo..."}
          </SectionSub>
          <AuthorChip
            type="button"
            $active={!selectedAuthorId}
            onClick={() => actions.setSelectedAuthorId("")}
          >
            <AuthorAvatarFallback>—</AuthorAvatarFallback>
            None
          </AuthorChip>
          {authors.map((author) => {
            const initial = (author.fullName || "?").charAt(0).toUpperCase();
            return (
              <AuthorChip
                key={author.id}
                type="button"
                $active={selectedAuthorId === author.id}
                onClick={() =>
                  actions.setSelectedAuthorId(
                    selectedAuthorId === author.id ? "" : author.id
                  )
                }
              >
                {author.profilePhotoURL ? (
                  <AuthorAvatar
                    src={author.profilePhotoURL}
                    alt={author.fullName}
                  />
                ) : (
                  <AuthorAvatarFallback>{initial}</AuthorAvatarFallback>
                )}
                {author.fullName || "Untitled"}
              </AuthorChip>
            );
          })}
        </AuthorRow>
        <SectionSub style={{ fontSize: 11 }}>
          Picked author's face is rendered into every generated ad image. The
          format's visual direction shapes the environment around them.
        </SectionSub>
      </SectionCard>

      {errorMsg && <Error>{errorMsg}</Error>}

      {/* ── Competitor reference ads (style anchors) ── */}
      <SectionCard>
        <SectionTitle>
          Competitor reference ads ({competitorRefs.length})
        </SectionTitle>
        <SectionSub>
          Upload real screenshots from Meta Ad Library, TikTok Creative
          Center, or Foreplay. Every reference is passed to the image model
          as a style anchor so generated ads look like proven winners in
          this niche instead of generic AI slop. Best with 3-8 references
          per platform.
        </SectionSub>
        <Row>
          <TextField
            placeholder="Label (e.g. AG1 testimonial)"
            value={newRefLabel}
            onChange={(e) => actions.setNewRefLabel(e.target.value)}
            aria-label="Reference label"
          />
          <TextField
            placeholder="Source (e.g. Meta Ad Library)"
            value={newRefSource}
            onChange={(e) => actions.setNewRefSource(e.target.value)}
            aria-label="Reference source"
          />
          <label>
            <Button
              variant="primary"
              size="sm"
              isLoading={isUploadingRef}
              disabled={isUploadingRef}
              onClick={() => {
                const input = document.getElementById(
                  "competitor-ref-input"
                ) as HTMLInputElement | null;
                input?.click();
              }}
            >
              Upload reference
            </Button>
          </label>
          <HiddenFileInput
            id="competitor-ref-input"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              handleUploadCompetitorRef(file);
              e.target.value = "";
            }}
          />
        </Row>
        {competitorRefs.length === 0 ? (
          <SectionSub>
            No references yet. Add a few proven winners from your niche to
            anchor the visual style.
          </SectionSub>
        ) : (
          <ImageGrid>
            {competitorRefs.map((r) => {
              const failure = refFetchFailures[r.imageURL];
              const loaded = Boolean(refDataUrlCache[r.imageURL]);
              return (
                <RefThumbCard key={r.id} $failed={Boolean(failure)}>
                  <RefThumbDelete
                    type="button"
                    aria-label="Delete reference"
                    onClick={() => handleDeleteCompetitorRef(r)}
                  >
                    ×
                  </RefThumbDelete>
                  <RefThumbImg src={r.imageURL} alt={r.label || "Reference"} />
                  {(r.label || r.source) && (
                    <RefThumbMeta>
                      {r.label && <div>{r.label}</div>}
                      {r.source && (
                        <div style={{ opacity: 0.7 }}>{r.source}</div>
                      )}
                    </RefThumbMeta>
                  )}
                  {failure ? (
                    <RefFailureBadge>
                      Browser can't fetch this — image gen won't use it.
                      Likely Storage CORS. ({failure.slice(0, 60)})
                    </RefFailureBadge>
                  ) : loaded ? (
                    <RefThumbMeta style={{ color: "var(--color-accent)" }}>
                      ✓ Ready
                    </RefThumbMeta>
                  ) : null}
                </RefThumbCard>
              );
            })}
          </ImageGrid>
        )}
      </SectionCard>

      {/* ── Ad format research ── */}
      <SectionCard>
        <SectionTitle>Winning ad formats</SectionTitle>
        <SectionSub>
          Research grounded in TikTok Creative Center, Meta Ad Library, and
          public examples. Saved to this community for re-use.
          {!hasAudienceData
            ? " Run audience research from the Slides tab first — this step needs persona + VOC as input."
            : ""}
        </SectionSub>
        <Row>
          <Button
            variant="primary"
            size="sm"
            isLoading={isResearching}
            disabled={!hasAudienceData || isResearching}
            onClick={handleResearchFormats}
          >
            🔎 Find winning ad formats
          </Button>
          {researchRationale && (
            <SectionSub style={{ flex: 1, minWidth: 200 }}>
              {researchRationale}
            </SectionSub>
          )}
        </Row>
        {researchSources.length > 0 && (
          <SectionSub style={{ fontSize: 11 }}>
            Sources: {researchSources.slice(0, 4).join(" · ")}
            {researchSources.length > 4 ? "..." : ""}
          </SectionSub>
        )}

        {discoveries.length > 0 && (
          <SectionSub>
            Found {discoveries.length} new format
            {discoveries.length === 1 ? "" : "s"} (saved to library below).
          </SectionSub>
        )}

        {adFormats.length === 0 ? (
          <SectionSub>
            No ad formats yet. Run the research above to discover them.
          </SectionSub>
        ) : (
          adFormats.map((f) => {
            const weight = profileWeightLookup.get(f.key);
            return (
              <FormatCard key={f.key}>
                <FormatHeader>
                  {f.icon && <Badge $tone="muted">{f.icon}</Badge>}
                  <FormatLabel>{f.label}</FormatLabel>
                  <Badge>{f.assetType}</Badge>
                  {f.platforms.map((p) => (
                    <Badge key={p} $tone="muted">
                      {p}
                    </Badge>
                  ))}
                  {f.targetLengthSec && (
                    <Badge $tone="muted">{f.targetLengthSec}s</Badge>
                  )}
                  {typeof weight === "number" && (
                    <Badge>{weight}% mix</Badge>
                  )}
                </FormatHeader>
                {f.lever && (
                  <SectionSub style={{ fontSize: 11 }}>
                    Lever: {f.lever}
                  </SectionSub>
                )}
                <FormatStructure>{f.structure}</FormatStructure>
                {f.evidence && (
                  <SectionSub style={{ fontSize: 10, fontStyle: "italic" }}>
                    {f.evidence}
                  </SectionSub>
                )}
                <Row>
                  <SectionSub style={{ marginRight: 4 }}>
                    Generate script:
                  </SectionSub>
                  {f.platforms.map((p) =>
                    selectedAngles.slice(0, 3).map((angle) => {
                      const k = `${f.key}-${p}-${angle}`;
                      return (
                        <Button
                          key={k}
                          size="sm"
                          variant="secondary"
                          isLoading={scriptLoadingKey === k}
                          disabled={!!scriptLoadingKey || !hasCampaignOrFallback}
                          onClick={() => handleGenerateScript(f, p, angle)}
                        >
                          {p} · {angle}
                        </Button>
                      );
                    })
                  )}
                </Row>

                {/* Image generation moved to the variant cards below — the
                    image belongs to a specific ad copy, not just the format. */}

                {Object.entries(activeScripts)
                  .filter(([k]) => k.startsWith(`${f.key}-`))
                  .map(([k, script]) => (
                    <FormatCard key={`script-${k}`}>
                      <FormatHeader>
                        <Badge>{script.platform}</Badge>
                        <Badge $tone="muted">{script.angle}</Badge>
                        <Badge $tone="muted">{script.totalLengthSec}s</Badge>
                      </FormatHeader>
                      {script.heroShot && (
                        <SectionSub style={{ fontSize: 11 }}>
                          Hero shot: {script.heroShot}
                        </SectionSub>
                      )}
                      {script.beats.map((beat, idx) => (
                        <BeatRow key={idx}>
                          <BeatTime>
                            {beat.startSec}-{beat.endSec}s
                          </BeatTime>
                          <BeatRole>{beat.role}</BeatRole>
                          <BeatBody>
                            {beat.spoken && (
                              <div>
                                <BeatLabel>spoken</BeatLabel>
                                {beat.spoken}
                              </div>
                            )}
                            {beat.textOverlay && (
                              <div>
                                <BeatLabel>overlay</BeatLabel>
                                {beat.textOverlay}
                              </div>
                            )}
                            {beat.shot && (
                              <div>
                                <BeatLabel>shot</BeatLabel>
                                {beat.shot}
                              </div>
                            )}
                          </BeatBody>
                        </BeatRow>
                      ))}
                      <SectionSub style={{ fontSize: 11 }}>
                        Final CTA: <strong>{script.finalCta}</strong>
                      </SectionSub>
                      <SectionSub style={{ fontSize: 11 }}>
                        Voiceover: {script.voiceoverScript}
                      </SectionSub>
                    </FormatCard>
                  ))}
              </FormatCard>
            );
          })
        )}
      </SectionCard>

      {/* ── Copy variant generation ── */}
      <SectionCard>
        <SectionTitle>Ad copy variations</SectionTitle>
        <SectionSub>
          Generates Meta primary text (125 visible) + headline (40) + description
          (30), and TikTok ad text (80). Each variation is one angle, char-guarded.
        </SectionSub>
        <HintCard>
          <HintTitle>Creative testing hierarchy</HintTitle>
          <span>
            Concept / angle (biggest impact) → hook → visual → body → CTA.
            Vary one layer per test cycle; change angle for the biggest swings.
          </span>
        </HintCard>
        <Row>
          <SectionSub>Angles:</SectionSub>
          {ANGLES.map((a) => (
            <Chip
              key={a}
              type="button"
              $active={selectedAngles.includes(a)}
              onClick={() => toggleAngle(a)}
            >
              {a}
            </Chip>
          ))}
        </Row>
        <Row>
          <SectionSub>Variations per angle:</SectionSub>
          <NumberField
            value={variationsPerAngle}
            onChange={(e) =>
              actions.setVariationsPerAngle(
                Math.max(1, Math.min(5, Number(e.target.value) || 3))
              )
            }
          />
          <Button
            variant="primary"
            size="sm"
            isLoading={isGeneratingCopy}
            disabled={
              !hasCampaignOrFallback ||
              isGeneratingCopy ||
              selectedPlatforms.length === 0 ||
              selectedAngles.length === 0
            }
            onClick={handleGenerateCopy}
          >
            Generate ad copy
          </Button>
          {variants.length > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleDownloadCsv}
            >
              Download CSV ({variants.length})
            </Button>
          )}
        </Row>

        {(() => {
          const missing: string[] = [];
          if (!hasCampaignOrFallback)
            missing.push(
              "add a campaign description above OR set up the landing page (title + subtitle)"
            );
          if (selectedPlatforms.length === 0) missing.push("pick at least one platform");
          if (selectedAngles.length === 0) missing.push("pick at least one angle");
          if (missing.length === 0) return null;
          return (
            <SectionSub style={{ color: "var(--color-warning, #c08400)" }}>
              To enable Generate: {missing.join(" · ")}
            </SectionSub>
          );
        })()}

        {variants.length > 0 && (
          <Row>
            <SectionSub>Campaign name (paid-ads convention):</SectionSub>
            <TextField
              placeholder="Objective"
              value={namingObjective}
              onChange={(e) => actions.setNamingObjective(e.target.value)}
              aria-label="Campaign objective"
            />
            <TextField
              placeholder="Audience"
              value={namingAudience}
              onChange={(e) => actions.setNamingAudience(e.target.value)}
              aria-label="Campaign audience"
            />
            <TextField
              placeholder="Offer"
              value={namingOffer}
              onChange={(e) => actions.setNamingOffer(e.target.value)}
              aria-label="Campaign offer"
            />
            <SectionSub style={{ fontSize: 11 }}>
              CSV will include rows like{" "}
              <code>
                META_{namingObjective || "Conv"}_
                {namingAudience || "BroadAudience"}_
                {namingOffer || "Default"}_{new Date().getFullYear()}Q
                {Math.floor(new Date().getMonth() / 3) + 1}
              </code>
            </SectionSub>
          </Row>
        )}

        {variants.length > 0 && (
          <VariantGrid>
            {variants.map((v, i) => {
              const primaryLimit =
                v.platform === "meta"
                  ? AD_PLATFORM_SPECS.meta.primaryTextVisible
                  : AD_PLATFORM_SPECS.tiktok.adText;
              return (
                <VariantCard key={i}>
                  <FormatHeader>
                    <Badge>{v.platform}</Badge>
                    <Badge $tone="muted">{v.angle}</Badge>
                  </FormatHeader>
                  <FieldRow>
                    <FieldHeader>
                      <span>
                        {v.platform === "meta" ? "Primary text" : "Ad text"}
                      </span>
                      <span
                        style={{
                          color: v.primaryTextOverLimit
                            ? "var(--color-danger)"
                            : undefined,
                        }}
                      >
                        {v.primaryTextLen}/{primaryLimit}
                      </span>
                    </FieldHeader>
                    <FieldBody>{v.primaryText}</FieldBody>
                  </FieldRow>
                  {v.headline && (
                    <FieldRow>
                      <FieldHeader>
                        <span>Headline</span>
                        <span
                          style={{
                            color: v.headlineOverLimit
                              ? "var(--color-danger)"
                              : undefined,
                          }}
                        >
                          {v.headlineLen}/{AD_PLATFORM_SPECS.meta.headline}
                        </span>
                      </FieldHeader>
                      <FieldBody>{v.headline}</FieldBody>
                    </FieldRow>
                  )}
                  {v.description && (
                    <FieldRow>
                      <FieldHeader>
                        <span>Description</span>
                        <span
                          style={{
                            color: v.descriptionOverLimit
                              ? "var(--color-danger)"
                              : undefined,
                          }}
                        >
                          {v.descriptionLen}/
                          {AD_PLATFORM_SPECS.meta.description}
                        </span>
                      </FieldHeader>
                      <FieldBody>{v.description}</FieldBody>
                    </FieldRow>
                  )}
                  <FieldRow>
                    <FieldHeader>
                      <span>CTA</span>
                    </FieldHeader>
                    <FieldBody>{v.cta}</FieldBody>
                  </FieldRow>

                  {/* Per-variant image generation + save */}
                  {(() => {
                    const selection = getVariantSelection(i, v);
                    const platformFormats = adFormats.filter((f) =>
                      f.platforms.includes(v.platform)
                    );
                    const placements = PLACEMENTS_FOR_PLATFORM[v.platform];
                    const img = variantImages[i];
                    const savedId = savedVariantIds[i];
                    return (
                      <>
                        <FieldRow>
                          <FieldHeader>
                            <span>Visual</span>
                          </FieldHeader>
                          <Row>
                            <SmallSelect
                              aria-label="Format aesthetic"
                              value={selection.formatKey}
                              onChange={(e) =>
                                actions.setVariantSelection(i, {
                                  formatKey: e.target.value,
                                  placement: selection.placement,
                                })
                              }
                              disabled={platformFormats.length === 0}
                            >
                              {platformFormats.length === 0 ? (
                                <option value="">No formats</option>
                              ) : (
                                platformFormats.map((f) => (
                                  <option key={f.key} value={f.key}>
                                    {f.label}
                                  </option>
                                ))
                              )}
                            </SmallSelect>
                            <SmallSelect
                              aria-label="Placement"
                              value={selection.placement}
                              onChange={(e) =>
                                actions.setVariantSelection(i, {
                                  formatKey: selection.formatKey,
                                  placement: e.target.value as AdPlacement,
                                })
                              }
                            >
                              {placements.map((p) => (
                                <option key={p} value={p}>
                                  {AD_PLACEMENT_SPECS[p].label}
                                </option>
                              ))}
                            </SmallSelect>
                            <Button
                              size="sm"
                              variant="secondary"
                              isLoading={imageLoadingVariantIdx === i}
                              disabled={
                                imageLoadingVariantIdx !== null ||
                                !hasCampaignOrFallback ||
                                !selection.formatKey
                              }
                              onClick={() => handleGenerateVariantImage(i, v)}
                            >
                              {img ? "Regen" : "Generate"}
                            </Button>
                            <Button
                              size="sm"
                              variant={savedId ? "secondary" : "primary"}
                              isLoading={!!variantSaving[i]}
                              disabled={!!variantSaving[i] || !!savedId}
                              onClick={() => handleSaveVariant(i, v)}
                            >
                              {savedId ? "Saved ✓" : "Save"}
                            </Button>
                          </Row>
                          <Row>
                            <label
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 11,
                                color: "var(--color-text-secondary)",
                                cursor: "pointer",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={!!variantTextOnImage[i]}
                                onChange={(e) =>
                                  actions.setVariantTextOnImage(
                                    i,
                                    e.target.checked
                                  )
                                }
                              />
                              Bake hook text into image
                            </label>
                          </Row>
                          {variantTextOnImage[i] && (
                            <Row>
                              <TextField
                                placeholder='Headline text (≤6 words, e.g. "Stuck on Publish?")'
                                value={variantOverlayText[i] || ""}
                                onChange={(e) =>
                                  actions.setVariantOverlayText(
                                    i,
                                    e.target.value
                                  )
                                }
                                style={{ width: "100%", maxWidth: 320 }}
                                aria-label="Custom headline text"
                              />
                              <SectionSub style={{ fontSize: 10 }}>
                                {variantOverlayText[i]?.trim()
                                  ? "Custom text takes precedence over the auto-clipped hook."
                                  : `Auto-clip from hook: "${v.primaryText
                                      .trim()
                                      .split(/\s+/)
                                      .slice(0, 4)
                                      .join(" ")}"`}
                              </SectionSub>
                            </Row>
                          )}
                        </FieldRow>
                        {img && (
                          <ImageThumbWrap>
                            <ImageThumb src={img.dataUrl} alt="Generated ad" />
                            <ImageThumbCaption>
                              {AD_PLACEMENT_SPECS[img.placement].label}
                              {lastRefStats &&
                                lastRefStats.attempted > 0 &&
                                ` · ${lastRefStats.succeeded}/${lastRefStats.attempted} refs used`}
                            </ImageThumbCaption>
                          </ImageThumbWrap>
                        )}
                      </>
                    );
                  })()}
                </VariantCard>
              );
            })}
          </VariantGrid>
        )}
      </SectionCard>

      {/* ── Saved ad variants library ── */}
      <SectionCard>
        <SectionTitle>
          Saved ads ({savedVariants.length})
          {isLoadingSaved && " · loading..."}
        </SectionTitle>
        <SectionSub>
          Every saved variant lives at
          {" "}<code>Community/{communityId}/AdVariants/</code>. Images are
          uploaded to Firebase Storage so the same ad can be re-downloaded or
          re-rendered later.
        </SectionSub>
        {savedVariants.length === 0 ? (
          <SectionSub>
            No saved ads yet. Generate copy + image above, then click Save on a
            variant to put it here.
          </SectionSub>
        ) : (
          <VariantGrid>
            {savedVariants.map((s) => (
              <VariantCard key={s.id}>
                <FormatHeader>
                  <Badge>{s.platform}</Badge>
                  <Badge $tone="muted">{s.angle}</Badge>
                  {s.placement && (
                    <Badge $tone="muted">
                      {AD_PLACEMENT_SPECS[s.placement]?.label || s.placement}
                    </Badge>
                  )}
                </FormatHeader>
                {s.imageURL && (
                  <ImageThumbWrap>
                    <ImageThumb src={s.imageURL} alt="Saved ad" />
                  </ImageThumbWrap>
                )}
                <FieldRow>
                  <FieldHeader>
                    <span>{s.platform === "meta" ? "Primary text" : "Ad text"}</span>
                  </FieldHeader>
                  <FieldBody>{s.primaryText}</FieldBody>
                </FieldRow>
                {s.headline && (
                  <FieldRow>
                    <FieldHeader>
                      <span>Headline</span>
                    </FieldHeader>
                    <FieldBody>{s.headline}</FieldBody>
                  </FieldRow>
                )}
                {s.description && (
                  <FieldRow>
                    <FieldHeader>
                      <span>Description</span>
                    </FieldHeader>
                    <FieldBody>{s.description}</FieldBody>
                  </FieldRow>
                )}
                <FieldRow>
                  <FieldHeader>
                    <span>CTA</span>
                  </FieldHeader>
                  <FieldBody>{s.cta}</FieldBody>
                </FieldRow>
                <Row>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleDeleteSaved(s)}
                  >
                    Delete
                  </Button>
                </Row>
              </VariantCard>
            ))}
          </VariantGrid>
        )}
      </SectionCard>
    </Root>
  );
};

export default AdsGenerator;
